import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Button, Toaster, Icon, Checkbox, Menu, MenuItem } from '@blueprintjs/core';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { DrawerSize, Position, Colors, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { setPlanVisibleLayers } from '$root/store/actions';
import { useAuth } from '$root/contexts/auth';


const Frame = styled.div.attrs(props => ({
    darkMode: props.darkMode || false,
    maxHeight: props.maxHeight || '320px',
}))`
    color: ${props => props.darkMode ? Colors.DARK_GRAY5 : Colors.GRAY5};
    background-color: ${props => props.darkMode ? Colors.GRAY5 : Colors.DARK_GRAY5};
    
    max-height: ${props => props.maxHeight};
    
    display: flex;
    flex-direction: column;
`;


const FrameToolbar = styled.div.attrs(props => ({
}))`
    display: flex;
    flex-direction: row;
    margin-bottom: 6px;
    
    & > * {
        margin-left: 8px;
    }
    
    & :nth-child(1) {
        margin-left: 0;
    }
`;


const FrameContent = styled.div.attrs(props => ({
    darkMode: props.darkMode || false,
}))`
    color: ${props => props.darkMode ? Colors.DARK_GRAY5 : Colors.BLACK};
    background-color: ${props => props.darkMode ? Colors.GRAY5 : Colors.WHITE};
    
    padding-top: 6px;
    padding-left: 4px;
    overflow: hidden auto;
`;


export default function MultiSelectPopup(props) {

    const { items, itemsInitState, darkMode } = props;
    const { keepalive } = useAuth();

    const dispatch = useDispatch();
    const toasterRef = useRef();

    const [ isOpen, setOpen ] = useState(false);
    const [ selectedItems, setSelectedItems ] = useState(itemsInitState || []);


    const onSelectAll = useCallback(select => {
        if (select) {
            setSelectedItems(items.map(i => i.id));
        }
        else {
            setSelectedItems([]);
        }
    }, [items, setSelectedItems]);


    const onSelectItem = useCallback((id, select) => {
        const idx = selectedItems.findIndex(itemId => itemId === id);
        if (select)
        {
            if (idx === -1)
                setSelectedItems(old => ([...old, id]));
        }
        else
        {
            if (idx !== -1)
                setSelectedItems(old => old.filter(itemId => itemId !== id));
        }
    }, [selectedItems, setSelectedItems]);


    useEffect(() => {
        setSelectedItems(itemsInitState);
    }, [itemsInitState, setSelectedItems]);


    useEffect(() => {

        if (!isOpen)
            return;

        window.addEventListener('keydown', handleOpenCloseClick);

        return () => {
            window.addEventListener('keydown', handleOpenCloseClick);
        };

    }, [ isOpen, handleOpenCloseClick]);


    const handleOpenCloseClick = useCallback((evt) => {
        if (!evt || !evt.type)
            return;

        keepalive();

        const isEscape = evt.type === 'keydown' && evt.key === 'Escape';
        const isClick = evt.type === 'click';
        if (!isEscape && !isClick)
            return;

        if (isOpen || isEscape)
        {
            setOpen(false);
            setSelectedItems(itemsInitState);
        }
        else
        {
            setOpen(true);
        }
    }, [isOpen, setOpen, itemsInitState, setSelectedItems]);


    const handleAcceptClick = useCallback(() => {
        keepalive();

        if (!toasterRef.current)
        {
            toasterRef.current = Toaster.create({
                canEscapeKeyClear:true,
                position:Position.TOP,
                usePortal:true
            });
        }

        if (selectedItems.length === 0)
        {
            toasterRef.current.show({
                message:`* ${'No layer selected'}`,
                intent:Intent.DANGER,
                timeout:3000
            });
            return;
        }

        setOpen(false);
        dispatch(setPlanVisibleLayers(selectedItems));

    }, [setOpen, selectedItems]);


    const handleSelectAllAndAcceptClick = useCallback(() => {
        keepalive();
        setOpen(false);
        dispatch(setPlanVisibleLayers(items.map(i => i.id)));
    }, [setOpen, items]);


    const layerMenuItems = items.map(grp => {
        const checked = selectedItems.findIndex(id => id === grp.id) !== -1;

        return <Checkbox key={grp.id}
                         checked={checked}
                         large={false}
                         style={{whiteSpace: 'nowrap', color: 'black'}}
                         label={grp.name}
                         onChange={(evt) => onSelectItem(grp.id,evt.target.checked)}
               />
    });


    return (
        <>
            <Popover2
                isOpen={isOpen}
                canEscapeKeyClose
                hasBackdrop
                content={
                <Frame style={{padding:6}}>
                    <FrameToolbar>
                        <Tooltip2 content={'Select none'} disabled={window.isTablet}>
                            <Button icon={IconNames.SQUARE}
                                    onClick={() => onSelectAll(false)}
                            />
                        </Tooltip2>
                        <Tooltip2 content={'Select all'} disabled={window.isTablet}>
                            <Button icon={IconNames.TICK}
                                    onClick={() => onSelectAll(true)}
                            />
                        </Tooltip2>
                        <div style={{width:'100%'}} />
                        <Tooltip2 content={'Show all'} disabled={window.isTablet}>
                            <Button icon={IconNames.LIGHTBULB}
                                    intent={Intent.SUCCESS}
                                    onClick={handleSelectAllAndAcceptClick}
                            />
                        </Tooltip2>
                        <Tooltip2 content={'Show selected'} disabled={window.isTablet}>
                            <Button icon={IconNames.TICK}
                                    intent={Intent.SUCCESS}
                                    onClick={handleAcceptClick}
                                    disabled={selectedItems.length === 0}
                            />
                        </Tooltip2>
                    </FrameToolbar>
                    <FrameContent>
                        {layerMenuItems}
                    </FrameContent>
                </Frame>
                }
            >
                <Tooltip2 content={'Layers'} disabled={window.isTablet}>
                    <Button icon={IconNames.LAYERS}
                        onClick={handleOpenCloseClick}
                    />
                </Tooltip2>
            </Popover2>
        </>
    );
}